<template>
  <div class="comment-card">
    <h2 class="comment-title">{{ $t('session.chat.comment') }}</h2>

    <div class="rating-control">
      <div class="row-of-stars" role="radiogroup" :aria-label="$t('session.chat.comment')">
        <button
          v-for="n in 5"
          :key="n"
          type="button"
          class="star-button"
          role="radio"
          :aria-checked="rateScore === n"
          :aria-label="`${n}/5`"
          :disabled="loading || saveSuccess"
          @click="handleRate(n)"
          @mouseenter="handleHover(n)"
          @mouseleave="handleHover(0)"
        >
          <StarIcon :status="hoverIndex > 0 ? n <= hoverIndex : n <= rateScore" />
        </button>
      </div>

      <span class="comment-label" aria-live="polite">{{ commentLabel }}</span>
    </div>

    <textarea
      v-model="commentValue"
      :placeholder="$t('session.chat.comment.placholder')"
      class="comment-textarea"
      :disabled="saveSuccess"
    ></textarea>

    <button
      type="button"
      class="submit-button"
      :class="{ 'submit-over': saveSuccess, loading }"
      :disabled="isSubmitDisabled"
      @click="handleSubmit"
    >
      <span>{{ $t(saveSuccess ? 'session.chat.comment.appr' : 'common.send') }}</span>
    </button>
  </div>
</template>

<script lang="ts">
/**
 * Purpose: Collect and submit a five-star rating for the active chat without changing the
 * backend grade-message or request contracts.
 * State: Rating, comment, loading, and submitted state stay local to this system-message card.
 * Props: content contains the normalized optional label for each score from 1 through 5.
 */
import { computed, defineComponent, type PropType, ref } from 'vue'
import { submitChatGrade } from '../../../api/simple-api'
import { message } from '../../../hooks/message'
import { useI18n } from '../../../i18n/simple-i18n'
import { useChatStore } from '../../../store/chat'
import { getChannelId } from '../../../utils/channelStorage'
import StarIcon from './StarIcon.vue'

type RatingScore = 1 | 2 | 3 | 4 | 5
type RatingContent = Record<`${RatingScore}`, string>

export default defineComponent({
  name: 'CommentCard',
  components: { StarIcon },
  props: {
    content: {
      type: Object as PropType<RatingContent>,
      required: true,
    },
  },
  setup(props) {
    const rateScore = ref(0)
    const hoverIndex = ref(0)
    const commentValue = ref('')
    const saveSuccess = ref(false)
    const loading = ref(false)
    const cStore = useChatStore()
    const { t } = useI18n()

    const isSubmitDisabled = computed(
      () => rateScore.value === 0 || loading.value || saveSuccess.value,
    )

    const handleRate = (rate: RatingScore) => {
      if (loading.value || saveSuccess.value) return
      rateScore.value = rateScore.value === rate ? 0 : rate
    }

    const handleHover = (rate: number) => {
      if (loading.value || saveSuccess.value) return
      hoverIndex.value = rate
    }

    const commentLabel = computed(() => {
      const score = hoverIndex.value > 0 ? hoverIndex.value : rateScore.value
      switch (score) {
        case 1:
          return props.content['1'] || t('session.chat.comment.zero')
        case 2:
          return props.content['2'] || t('session.chat.comment.zero')
        case 3:
          return props.content['3'] || t('session.chat.comment.zero')
        case 4:
          return props.content['4'] || t('session.chat.comment.zero')
        case 5:
          return props.content['5'] || t('session.chat.comment.zero')
        default:
          return t('session.chat.comment.zero')
      }
    })

    /**
     * Submits one non-zero score for the active chat. The card remains editable after transport or
     * backend failure, and locks only after the backend confirms success.
     */
    const handleSubmit = async () => {
      if (isSubmitDisabled.value) return

      loading.value = true
      try {
        const response = await submitChatGrade(getChannelId(), {
          chatId: cStore.chatId,
          grade: rateScore.value,
          comment: commentValue.value,
        })
        const responseBody: unknown = await response.json()
        const responseCode =
          typeof responseBody === 'object' && responseBody !== null
            ? Reflect.get(responseBody, 'code')
            : undefined
        const responseMessage =
          typeof responseBody === 'object' && responseBody !== null
            ? Reflect.get(responseBody, 'msg')
            : undefined

        if (responseCode === '00000') {
          saveSuccess.value = true
          hoverIndex.value = 0
          return
        }

        message.warning(
          typeof responseMessage === 'string' ? responseMessage : t('session.chat.comment.failed'),
        )
      } catch {
        message.warning(t('session.chat.comment.failed'))
      } finally {
        loading.value = false
      }
    }

    return {
      commentLabel,
      commentValue,
      handleHover,
      handleRate,
      handleSubmit,
      hoverIndex,
      isSubmitDisabled,
      loading,
      rateScore,
      saveSuccess,
    }
  },
})
</script>

<style lang="less" scoped>
.comment-card {
  box-sizing: border-box;
  display: flex;
  width: 100%;
  padding: 20px;
  flex-direction: column;
  align-items: center;
  border: 1px solid var(--border-default, #edefed);
  border-radius: 8px;
  background: #fff;
}

.comment-title {
  align-self: stretch;
  margin: 0 0 32px;
  color: var(--text-primary, #202221);
  font-family: inherit;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 24px;
  text-align: center;
}

.rating-control {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.row-of-stars {
  display: flex;
  align-items: center;
  gap: var(--abot-rating-star-gap, 20px);
}

.star-button {
  display: grid;
  width: 36px;
  height: 36px;
  padding: 0;
  place-items: center;
  border: 0;
  background: transparent;
  cursor: pointer;

  &:focus-visible {
    border-radius: 4px;
    outline: 2px solid var(--abot-brand-color);
    outline-offset: 2px;
  }

  &:disabled {
    cursor: default;
  }
}

.comment-label {
  min-height: 22px;
  color: var(--text-primary, #202221);
  font-size: 14px;
  font-weight: 400;
  line-height: 22px;
  text-align: center;
}

.comment-textarea {
  box-sizing: border-box;
  width: 100%;
  height: 80px;
  margin-top: 20px;
  padding: 5px 12px;
  overflow-y: auto;
  resize: none;
  border: 0;
  border-radius: 4px;
  outline: 0;
  background: var(--neutral-fill-2, #f4f6f5);
  color: var(--text-primary, #202221);
  font-family: inherit;
  font-size: 14px;
  font-weight: 400;
  line-height: 22px;

  &::placeholder {
    color: var(--text-4, #b8bbb8);
    opacity: 1;
  }

  &:focus-visible {
    outline: 2px solid var(--abot-brand-color);
    outline-offset: 2px;
  }

  &:disabled {
    opacity: 1;
    -webkit-text-fill-color: var(--text-primary, #202221);
  }
}

.submit-button {
  box-sizing: border-box;
  display: flex;
  width: 100%;
  margin-top: 20px;
  padding: 8px 0;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 4px;
  background: var(--abot-brand-color, #2e9b4a);
  color: #fff;
  cursor: pointer;

  & > span {
    color: currentColor;
    font-family: inherit;
    font-size: 14px;
    font-weight: 400;
    line-height: 22px;
  }

  &:focus-visible {
    outline: 2px solid var(--abot-brand-color);
    outline-offset: 2px;
  }

  &:disabled,
  &.loading {
    background: var(--abot-brand-surface);
    color: var(--abot-brand-color);
    cursor: not-allowed;
  }

  &.submit-over {
    background: var(--neutral-fill-2, #f4f6f5);
    color: var(--text-primary, #202221);
  }
}
</style>
